/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useAvroTracking } from '../../observability';
import { MEDIA_MIN_WIDTH } from '../../constants';

const GOOGLE_SSO_URL = '/google-sso/start?scopes=basic';

type Props = { text?: string };

const Styles = labelStyles('GoogleSSOButton', {
  root: css({
    backgroundColor: 'rgb(66, 133, 244)',
    border: 'none',
    minHeight: '50px',
    borderRadius: '5px',
    [MEDIA_MIN_WIDTH.large]: {
      height: '70px',
    },
    '&:hover:not([disabled])': {
      backgroundColor: 'rgb(51, 103, 214)',
      boxShadow: 'none',
      color: Colors.neutral100,
      borderColor: Colors.night,
    },
  }),
  link: css({
    ...Typography.heading,
    height: '100%',
    padding: '0 12px',
    display: 'grid',
    gridTemplateColumns: '32px 1fr 32px',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 500,
    fontSize: '16px',
    textDecoration: 'none',
    textAlign: 'center',
    color: Colors.neutral100,

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.subHeader3Medium,
      padding: '0 20px',
    },
  }),
  logo: css({
    height: '32px',
    display: 'grid',
    placeItems: 'center',
    backgroundColor: Colors.neutral100,
    borderRadius: '5px',
  }),
});

function GoogleLogo() {
  return (
    <svg
      height="21"
      preserveAspectRatio="xMidYMid"
      viewBox="0 0 256 262"
      width="21"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Google Logo</title>
      <path
        d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
        fill="#4285F4"
      />
      <path
        d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
        fill="#34A853"
      ></path>
      <path
        d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
        fill="#FBBC05"
      ></path>
      <path
        d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
        fill="#EB4335"
      ></path>
    </svg>
  );
}

export function GoogleSSOButton({ text = 'Start free with Google' }: Props) {
  const { emitSignupUserInteractionEvent } = useAvroTracking();

  const handleClick = () => {
    emitSignupUserInteractionEvent({
      absolute_url: GOOGLE_SSO_URL,
      signup_funnel: 'signup_intent',
      signup_type: 'google_sso',
    });
  };

  return (
    <div css={Styles.root}>
      <a href={GOOGLE_SSO_URL} onClick={handleClick} css={Styles.link}>
        <span css={Styles.logo}>
          <GoogleLogo />
        </span>{' '}
        {text}
      </a>
    </div>
  );
}
